import type { ProjectSuggestion } from 'api/agentSessions/projectSuggestions.api';
import type { AgentPeriod } from './agentTracking.model';

export const MIN_PROJECT_SUGGESTION_SCORE = 0.3;
export const CLOSE_PROJECT_SCORE_GAP = 0.1;
export function projectChoices(suggestion: ProjectSuggestion) {
  const candidates =
    suggestion.candidates ??
    (suggestion.project_id === null
      ? []
      : [
          {
            project_id: suggestion.project_id,
            project_name: suggestion.project_name ?? 'Project',
            probability: suggestion.probability,
          },
        ]);
  return candidates
    .filter(p => p.probability !== undefined && p.probability >= MIN_PROJECT_SUGGESTION_SCORE)
    .sort((a, b) => (b.probability ?? 0) - (a.probability ?? 0))
    .slice(0, 3);
}
export function hasDisplayableProject(suggestion: ProjectSuggestion) {
  return suggestion.status === 'predicted' && projectChoices(suggestion).length > 0;
}
export function closeProjectChoice(suggestion: ProjectSuggestion, id: number) {
  const choices = projectChoices(suggestion);
  const choice = choices.find(p => p.project_id === id);
  if (id === suggestion.project_id) return true;
  return (
    choice?.probability !== undefined &&
    choices[0]?.probability !== undefined &&
    choices[0].probability - choice.probability <= CLOSE_PROJECT_SCORE_GAP + 1e-9
  );
}
export function projectChoiceGroup(suggestion: ProjectSuggestion) {
  return suggestion.project_id;
}
/** Display summary only; assignment thresholds continue to use each period's score. */
export function averageProjectScore(scores: (number | undefined)[]) {
  const values = scores.filter((p): p is number => p !== undefined && Number.isFinite(p));
  if (!values.length) return null;
  const average = values.reduce((sum, value) => sum + value, 0) / values.length;
  return average > 0 && average < 0.01 ? '<1%' : `${Math.round(average * 100)}%`;
}

const PENDING_PREDICTION = ['waiting_summary', 'queued', 'running'];
/**
 * Unassigned periods without a displayable suggestion of their own (no summary,
 * no clear match, nothing predicted yet). Accepting the session's leading
 * project carries them along. Periods waiting for a summary or prediction stay
 * separate, so accepting an earlier suggestion cannot assign newer activity.
 */
export function unmatchedPeriods(
  periods: AgentPeriod[],
  suggestions: Record<number, ProjectSuggestion>
) {
  return periods.filter(p => {
    const suggestion = suggestions[p.segmentId];
    return (
      p.projectId === null &&
      !(suggestion && hasDisplayableProject(suggestion)) &&
      !PENDING_PREDICTION.includes(p.projectPredictionState ?? '')
    );
  });
}
/** The suggested project covering the most tracked time; ties go to more periods, then list order. */
export function leadingProjectGroup<T>(groups: { id: T; members: AgentPeriod[] }[]) {
  const weight = (members: AgentPeriod[]) =>
    members.reduce((sum, p) => sum + Math.max(0, p.activeSeconds || 0), 0);
  return groups.reduce<(typeof groups)[number] | undefined>((best, group) => {
    if (!best) return group;
    const difference = weight(group.members) - weight(best.members);
    if (difference !== 0) return difference > 0 ? group : best;
    return group.members.length > best.members.length ? group : best;
  }, undefined)?.id;
}
